import QRCode from 'qrcode';
import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Form,
  Grid,
  Header,
  Card,
  Statistic,
  Divider,
  Modal,
} from 'semantic-ui-react';
import { API, showError, showInfo, showSuccess } from '../../helpers';
import { renderQuota } from '../../helpers/render';
import { useTranslation } from 'react-i18next';

const AMOUNT_OPTIONS = ['10', '30', '50', '100', '150', '200', '500', '1000'];
const SHOW_REDEMPTION_MODULES = false;

const getPaymentPayload = (responseData) => responseData?.data ?? responseData;

const getOrderNo = (payload) =>
  payload?.order_no || payload?.orderNo || payload?.order_number;

const getCodeUrl = (payload) =>
  payload?.code_url ||
  payload?.codeUrl ||
  payload?.qr_code ||
  payload?.qrCode ||
  payload?.qrcode_url ||
  payload?.pay_url ||
  payload?.payUrl;

const isOrderPaid = (responseData) => {
  if (responseData?.data === true) return true;
  const payload = getPaymentPayload(responseData);
  if (payload === true) return true;
  const status = String(
    payload?.status ||
      payload?.state ||
      payload?.pay_status ||
      payload?.trade_state ||
      payload?.tradeState ||
      ''
  ).toLowerCase();
  return (
    payload?.paid === true ||
    payload?.is_paid === true ||
    payload?.pay_success === true ||
    [
      'success',
      'paid',
      'completed',
      'finish',
      'finished',
      'ok',
      'trade_success',
      '已支付',
      '支付成功',
    ].includes(status)
  );
};

const TopUp = () => {
  const { t } = useTranslation();
  const [redemptionCode, setRedemptionCode] = useState('');
  const [topUpLink, setTopUpLink] = useState('');
  const [userQuota, setUserQuota] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [user, setUser] = useState({});
  const [selectedAmount, setSelectedAmount] = useState('10');
  const [customAmount, setCustomAmount] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrCodeImage, setQrCodeImage] = useState('');
  const [paymentOrderNo, setPaymentOrderNo] = useState('');
  const [pollingPayment, setPollingPayment] = useState(false);
  const pollingTimerRef = useRef(null);

  const clearPaymentPolling = () => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
    setPollingPayment(false);
  };

  const closePaymentModal = () => {
    clearPaymentPolling();
    setQrModalOpen(false);
    setQrCodeImage('');
    setPaymentOrderNo('');
  };

  const getCurrentAmount = () =>
    selectedAmount === 'custom' ? customAmount : selectedAmount;

  const getValidAmount = () => {
    const amount = String(getCurrentAmount()).trim();
    if (!/^\d+(\.\d{1,2})?$/.test(amount) || Number(amount) <= 0) {
      return '';
    }
    return Number(amount).toFixed(2);
  };

  const pollPaymentOrder = async (orderNo) => {
    const res = await API.get(
      `/api/payment/order/${encodeURIComponent(orderNo)}`
    );
    if (!res) return;
    const { success, message } = res.data;
    if (success === false) {
      showError(message);
      return;
    }
    if (isOrderPaid(res.data)) {
      closePaymentModal();
      showSuccess(t('topup.wechat_pay.payment_success'));
      await getUserQuota();
    }
  };

  const startPaymentPolling = (orderNo) => {
    clearPaymentPolling();
    setPollingPayment(true);
    pollPaymentOrder(orderNo).then();
    pollingTimerRef.current = setInterval(() => {
      pollPaymentOrder(orderNo).then();
    }, 3000);
  };

  const createWeChatPayment = async () => {
    const amount = getValidAmount();
    if (!amount) {
      showInfo(t('topup.wechat_pay.invalid_amount'));
      return;
    }
    setPaymentLoading(true);
    try {
      const res = await API.post('/api/payment/wechat/native', { amount });
      if (!res) return;
      const { success, message } = res.data;
      if (success === false) {
        showError(message);
        return;
      }
      const payload = getPaymentPayload(res.data);
      const orderNo = getOrderNo(payload);
      const codeUrl = getCodeUrl(payload);
      if (!orderNo || !codeUrl) {
        showError(t('topup.wechat_pay.invalid_order_response'));
        return;
      }
      const qrImage = await QRCode.toDataURL(codeUrl, {
        width: 260,
        margin: 2,
      });
      setPaymentOrderNo(orderNo);
      setQrCodeImage(qrImage);
      setQrModalOpen(true);
      startPaymentPolling(orderNo);
    } catch (err) {
      showError(err);
    } finally {
      setPaymentLoading(false);
    }
  };

  const topUp = async () => {
    if (redemptionCode === '') {
      showInfo(t('topup.redeem_code.empty_code'));
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await API.post('/api/user/topup', {
        key: redemptionCode,
      });
      const { success, message, data } = res.data;
      if (success) {
        showSuccess(t('topup.redeem_code.success'));
        setUserQuota((quota) => {
          return quota + data;
        });
        setRedemptionCode('');
      } else {
        showError(message);
      }
    } catch (err) {
      showError(t('topup.redeem_code.request_failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const openTopUpLink = () => {
    if (!topUpLink) {
      showError(t('topup.redeem_code.no_link'));
      return;
    }
    let url = new URL(topUpLink);
    let username = user.username;
    let user_id = user.id;
    url.searchParams.append('username', username);
    url.searchParams.append('user_id', user_id);
    url.searchParams.append('transaction_id', crypto.randomUUID());
    window.open(url.toString(), '_blank');
  };

  const getUserQuota = async () => {
    let res = await API.get(`/api/user/self`);
    const { success, message, data } = res.data;
    if (success) {
      setUserQuota(data.quota);
      setUser(data);
    } else {
      showError(message);
    }
  };

  useEffect(() => {
    let status = localStorage.getItem('status');
    if (status) {
      status = JSON.parse(status);
      if (status.top_up_link) {
        setTopUpLink(status.top_up_link);
      }
    }
    getUserQuota().then();

    return () => {
      clearPaymentPolling();
    };
  }, []);

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header>
            <Header as='h2'>{t('topup.title')}</Header>
          </Card.Header>

          <Card
            fluid
            style={{
              marginBottom: '1.5em',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <Card.Content>
              <Card.Header>
                <Header as='h3' style={{ color: '#2185d0', margin: '1em' }}>
                  <i className='credit card outline icon'></i>
                  {t('topup.wechat_pay.title')}
                </Header>
              </Card.Header>
              <Card.Description>
                <Form>
                  <Form.Field>
                    <label>{t('topup.wechat_pay.amount')}</label>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '0.75em',
                      }}
                    >
                      {AMOUNT_OPTIONS.map((amount) => (
                        <Button
                          key={amount}
                          basic={selectedAmount !== amount}
                          primary={selectedAmount === amount}
                          type='button'
                          active={selectedAmount === amount}
                          onClick={() => setSelectedAmount(amount)}
                        >
                          {t('topup.wechat_pay.amount_text', { amount })}
                        </Button>
                      ))}
                      <Button
                        basic={selectedAmount !== 'custom'}
                        primary={selectedAmount === 'custom'}
                        type='button'
                        active={selectedAmount === 'custom'}
                        onClick={() => setSelectedAmount('custom')}
                      >
                        {t('topup.wechat_pay.custom_amount')}
                      </Button>
                    </div>
                  </Form.Field>
                  {selectedAmount === 'custom' && (
                    <Form.Input
                      width={6}
                      type='number'
                      min='0.01'
                      step='0.01'
                      icon='yen sign'
                      iconPosition='left'
                      placeholder={t('topup.wechat_pay.custom_placeholder')}
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                    />
                  )}
                  <Divider />
                  <Form.Field>
                    <label>{t('topup.wechat_pay.payment_method')}</label>
                    <Button
                      type='button'
                      color='green'
                      active
                      icon='wechat'
                      content={t('topup.wechat_pay.method')}
                    />
                  </Form.Field>
                  <Button
                    primary
                    size='large'
                    onClick={createWeChatPayment}
                    loading={paymentLoading}
                    disabled={paymentLoading}
                  >
                    {t('topup.wechat_pay.pay_button')}
                  </Button>
                </Form>
              </Card.Description>
            </Card.Content>
          </Card>

          {SHOW_REDEMPTION_MODULES && (
            <Grid columns={2} stackable>
            <Grid.Column>
              <Card
                fluid
                style={{
                  height: '100%',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                }}
              >
                <Card.Content
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Card.Header>
                    <Header as='h3' style={{ color: '#2185d0', margin: '1em' }}>
                      <i className='credit card icon'></i>
                      {t('topup.get_code.title')}
                    </Header>
                  </Card.Header>
                  <Card.Description
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div style={{ textAlign: 'center', paddingTop: '1em' }}>
                        <Statistic>
                          <Statistic.Value style={{ color: '#2185d0' }}>
                            {renderQuota(userQuota, t)}
                          </Statistic.Value>
                          <Statistic.Label>
                            {t('topup.get_code.current_quota')}
                          </Statistic.Label>
                        </Statistic>
                      </div>

                      <div
                        style={{ textAlign: 'center', paddingBottom: '1em' }}
                      >
                        <Button
                          primary
                          size='large'
                          onClick={openTopUpLink}
                          style={{ width: '80%' }}
                        >
                          {t('topup.get_code.button')}
                        </Button>
                      </div>
                    </div>
                  </Card.Description>
                </Card.Content>
              </Card>
            </Grid.Column>

            <Grid.Column>
              <Card
                fluid
                style={{
                  height: '100%',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                }}
              >
                <Card.Content
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Card.Header>
                    <Header as='h3' style={{ color: '#21ba45', margin: '1em' }}>
                      <i className='ticket alternate icon'></i>
                      {t('topup.redeem_code.title')}
                    </Header>
                  </Card.Header>
                  <Card.Description
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Form.Input
                        fluid
                        icon='key'
                        iconPosition='left'
                        placeholder={t('topup.redeem_code.placeholder')}
                        value={redemptionCode}
                        onChange={(e) => {
                          setRedemptionCode(e.target.value);
                        }}
                        onPaste={(e) => {
                          e.preventDefault();
                          const pastedText = e.clipboardData.getData('text');
                          setRedemptionCode(pastedText.trim());
                        }}
                        action={
                          <Button
                            icon='paste'
                            content={t('topup.redeem_code.paste')}
                            onClick={async () => {
                              try {
                                const text =
                                  await navigator.clipboard.readText();
                                setRedemptionCode(text.trim());
                              } catch (err) {
                                showError(t('topup.redeem_code.paste_error'));
                              }
                            }}
                          />
                        }
                      />

                      <div style={{ paddingBottom: '1em' }}>
                        <Button
                          color='green'
                          fluid
                          size='large'
                          onClick={topUp}
                          loading={isSubmitting}
                          disabled={isSubmitting}
                        >
                          {isSubmitting
                            ? t('topup.redeem_code.submitting')
                            : t('topup.redeem_code.submit')}
                        </Button>
                      </div>
                    </div>
                  </Card.Description>
                </Card.Content>
              </Card>
            </Grid.Column>
            </Grid>
          )}
        </Card.Content>
      </Card>
      <Modal
        open={qrModalOpen}
        onClose={closePaymentModal}
        size='tiny'
        style={{ maxWidth: '420px' }}
      >
        <Modal.Header>{t('topup.wechat_pay.modal_title')}</Modal.Header>
        <Modal.Content>
          <div style={{ textAlign: 'center' }}>
            {qrCodeImage && (
              <img
                src={qrCodeImage}
                alt={t('topup.wechat_pay.qr_alt')}
                style={{ width: 260, height: 260 }}
              />
            )}
            <p style={{ marginTop: '1em' }}>
              {t('topup.wechat_pay.modal_tip')}
            </p>
            {paymentOrderNo && (
              <p style={{ color: '#666', wordBreak: 'break-all' }}>
                {t('topup.wechat_pay.order_no', {
                  order_no: paymentOrderNo,
                })}
              </p>
            )}
            {pollingPayment && (
              <p style={{ color: '#2185d0' }}>
                {t('topup.wechat_pay.polling')}
              </p>
            )}
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={closePaymentModal}>
            {t('topup.wechat_pay.close')}
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default TopUp;
